import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import { updateStatement } from 'actions';
import PaymentTypes from 'common/Forms/PaymentTypes';
import FatcaExemptions from 'common/Forms/FatcaExemptions';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import ModalWrapper from 'components/Modals/ModalWrapper';

const PooledRatesModal = ({ structureId, accountId, statementVersion, isShown, onClose, permissions }) => {
  const { pooledRates } = useSelector(state => state && state.forms);
  const { selectedPooledAccount } = useSelector(state => state && state.forms);
  const [ratesList, setRatesList] = useState([]);
  const [paymentType, setPaymentType] = useState('');
  const [newRate, setNewRate] = useState(null);
  const [exemptionCode, setExemptionCode] = useState(0);
  const [ratesChanged, setRatesChanges] = useState(false);
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState([]);
  const [rateError, setRateError] = useState(false);
  const provideExemption = newRate === 0 && exemptionCode === 0;
  const hasPermission = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]);
  const showingVersion = statementVersion !== null && typeof statementVersion !== 'undefined';

  const setRate = (value) => {
    if (value && !Number.isNaN(value)) {
      setNewRate(parseFloat(value));
      if (parseFloat(value) > 30) {
        setRateError(true);
        return;
      }
    } else {
      setNewRate(null);
    }
    setRateError(false);
  };

  const dispatch = useDispatch();

  const closeModal = () => {
    setRate(null);
    setExemptionCode(null);
    setPaymentType(null);

    onClose();
  };

  const deleteItem = (index) => {
    const items = [...ratesList];
    items.splice(index, 1);
    setRatesList(items);
    setRatesChanges(true);
    setAvailablePaymentTypes(PaymentTypes.filter(x => items.findIndex(y => y.paymentType === x.key) < 0 && x.isPooled));
  };

  const addNewRate = () => {
    const newRatesList = ratesList;
    const newPayment = {
      paymentType,
      rate: newRate,
      exemptionCode
    };

    if (newRate > 30) {
      toastr.error('Data Entry Error', 'The \'Rate\' value can not be above 30%.');
      return;
    }

    newRatesList.push(newPayment);
    setRatesList(newRatesList);
    setRatesChanges(true);
    setAvailablePaymentTypes(PaymentTypes.filter(x => newRatesList.findIndex(y => y.paymentType === x.key) < 0 && x.isPooled));
    setPaymentType('');
    setExemptionCode(newRatesList[0].exemptionCode);
  };

  const submitRates = () => {
    const obj = {
      pooledRates: {
        guid: selectedPooledAccount,
        rates: ratesList
      }
    };

    const rateKeys = ratesList.map(x => x.paymentType);
    const newPaymentTypeOptions = {
      structureId,
      paymentTypes: PaymentTypes.filter(x => rateKeys.includes(x.key)).map(x => x.value)
    };

    dispatch(updateStatement(accountId, obj, newPaymentTypeOptions));
    setRatesChanges(false);
  };

  useEffect(() => {
    if (pooledRates) {
      setRatesList([...pooledRates]);
    } else {
      setRatesList([]);
    }

    return function cleanup() {
      setRate(null);
      setExemptionCode(null);
    };
  }, [pooledRates]);

  useEffect(() => {
    if (ratesList) {
      setAvailablePaymentTypes(PaymentTypes.filter(x => ratesList.findIndex(y => y.paymentType === x.key) < 0 && x.isPooled));

      if (ratesList.length > 0) {
        setNewRate(ratesList[0].rate);
        setExemptionCode(ratesList[0].exemptionCode);
      }
    }
  }, [ratesList]);

  return (
    <ModalWrapper
      isOpen={ isShown }
      title="Withholding Rates"
      btnConfirmTitle="Save"
      btnCancelTitle={ ratesChanged ? 'Close Without Saving' : 'Close' }
      onCancel={ e => closeModal() }
      showFooter
      disableConfirm={ () => !ratesChanged }
      modalSize="modal-lg whd-rates-modal"
      onConfirm={ () => submitRates(accountId, selectedPooledAccount, ratesList) }
    >
      { (ratesList === null || (ratesList && ratesList.length <= 0)) && <h1>No withholding rates to display</h1> }
      { ratesList != null && ratesList.length > 0 && <table className="table table-striped table-hover table-bordered">
        <thead>
          <tr>
            <th>Payment type</th>
            <th>Rate</th>
            <th>Exemption Code</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          { ratesList.map((pooledRate, index) => (pooledRate
              && <tr key={ pooledRate.paymentType }>
                <td>{ pooledRate.paymentType && PaymentTypes.findByKey(pooledRate.paymentType) }</td>
                <td>{ pooledRate.rate ? `${ pooledRate.rate.toFixed(2) }%` : '0.00%' }</td>
                <td>{ pooledRate.exemptionCode && pooledRate.exemptionCode > 0 ? FatcaExemptions.findByKey(pooledRate.exemptionCode) : 'None' }</td>
                <td>{ !showingVersion && hasPermission && <button className="btn btn-sm btn-danger" type="button" onClick={ () => deleteItem(index) }><span className="icon icon-delete" /></button> }</td>
              </tr>
          )) }
        </tbody>
      </table> }
      { !showingVersion && hasPermission && <div>
        <p>Please add a new rate. Payment types can only be selected once and an exemption code is required for rates of 0%</p>
        <div className="form-group">
          <div className="col-3 mr-1">
            <select className="form-select" value={ paymentType ?? '' } onChange={ e => setPaymentType(parseInt(e.target.value, 10)) } data-testid="dropdown-select-payment-type">
              <option value="" disabled>Select Payment Type...</option>
              { availablePaymentTypes.map(x => (
                <option key={ x.key } value={ x.key }> { x.value } </option>
              )) }
            </select>
          </div>
          <div className="col-3 mr-1">
            <input
              className={ `form-input ${ rateError ? 'is-error' : '' }` }
              type="number"
              step="0.01"
              value={ newRate != null ? newRate.toString() : '' }
              placeholder="Rate"
              onChange={ e => setRate(e.target.value) }
              data-testid="input-whd-rates"
            />
          </div>
          <select className="form-select col-3 mr-1" value={ exemptionCode ?? '0' } disabled={ newRate !== 0 } onChange={ e => setExemptionCode(parseInt(e.target.value, 10)) }>
            { FatcaExemptions.map(x => (
              <option key={ x.key } value={ x.key }> { x.value } </option>
            )) }
          </select>
          <div className="col-3">
            <button className="btn btn-primary" type="button" disabled={ paymentType === '' || newRate === null || provideExemption } onClick={ () => addNewRate() } data-testid="btn-whd-rates-add">Add</button>
          </div>
        </div>
      </div> }
    </ModalWrapper>
  );
};

PooledRatesModal.propTypes = {
  structureId: PropTypes.number.isRequired,
  accountId: PropTypes.number.isRequired,
  statementVersion: PropTypes.string,
  isShown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired
};

PooledRatesModal.defaultProps = {
  statementVersion: null
};

export default PooledRatesModal;
